import { useState } from 'react'

import { useTranslation } from 'react-i18next'

import { UpdateZoneRequestDTO } from '@/core/api/dtos/request/zone/update'
import { Zone } from '@/core/api/entities/zone/zone'
import { useAccountConfig } from '@/core/hooks/useAccountConfig'
import { useUpdateZone } from '@/core/hooks/useZone'
import { useNotificationContext } from '@/shared/context/NotificationProvider'

interface UseZoneEditProps {
	onSuccess?: () => void
	onError?: (error: Error) => void
}

export const useZoneEdit = (props?: UseZoneEditProps) => {
	const { onSuccess, onError } = props || {}
	const { showNotification } = useNotificationContext()
	const { t } = useTranslation('common')
	const [selectedZone, setSelectedZone] = useState<Zone | null>(null)
	const [isModalVisible, setIsModalVisible] = useState(false)

	const { mutate, isPending } = useUpdateZone({
		config: useAccountConfig({}),
		options: {
			onSuccess: () => {
				showNotification({
					type: 'success',
					message: t('entities.zone.edit.success.title'),
					description: t('entities.zone.edit.success.description', {
						name: selectedZone?.name,
					}),
				})

				setIsModalVisible(false)
				setSelectedZone(null)

				if (onSuccess) onSuccess()
			},
			onError: (error: Error) => {
				showNotification({
					type: 'error',
					message: t('entities.zone.edit.error.title'),
					description: t('entities.zone.edit.error.description', {
						name: selectedZone?.name,
					}),
				})

				if (onError) onError(error)
			},
		},
	})

	const openEditModal = (zone: Zone) => {
		setSelectedZone(zone)
		setIsModalVisible(true)
	}

	const closeEditModal = () => {
		setIsModalVisible(false)
		setSelectedZone(null)
	}

	const handleEditZone = (data: UpdateZoneRequestDTO) => {
		if (selectedZone?.id) {
			mutate({
				zoneId: selectedZone.id,
				data,
			})
		}
	}

	return {
		selectedZone,
		isModalVisible,
		isEditing: isPending,
		openEditModal,
		closeEditModal,
		handleEditZone,
	}
}

export default useZoneEdit
